import { FC, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { createUseStyles } from 'react-jss';
import { MicrophoneIcon } from '../theme/Icons';

const useStyles = createUseStyles({
    wrapper: {
        position: 'relative'
    },
    button: {
        width: 72,
        height: 72,
        borderRadius: '50%',
        backgroundColor: '#11789D',
        boxShadow: '0 4px 16px rgba(17,120,157,0.3)',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    // Fixed-positioned copy with transitions — applied immediately so the
    // browser paints it at the initial position before target values are set
    buttonFixed: ({ transitionMs }: { transitionMs: number }) => ({
        position: 'fixed',
        zIndex: 50001,
        transition: `transform ${transitionMs}ms cubic-bezier(0.4, 0, 0.2, 1), `
            + `left ${transitionMs}ms cubic-bezier(0.4, 0, 0.2, 1), `
            + `top ${transitionMs}ms cubic-bezier(0.4, 0, 0.2, 1), `
            + `background-color ${transitionMs}ms ease-out, `
            + `box-shadow ${transitionMs}ms ease-out`
    }),
    // Target state — applied after a frame so the transition animates to these values
    buttonAnimTarget: {
        // Scale down from 72px to 46px (nav FAB size): 46/72 ≈ 0.64
        transform: 'scale(0.64)',
        // Transition to recording (danger) color to match nav FAB
        backgroundColor: '#eb445a',
        boxShadow: '0 4px 16px rgba(235,68,90,0.3)'
    },
    label: {
        fontSize: 13,
        color: '#9aaaaf',
        marginTop: 12
    }
});

interface IIntroRecordButtonProps {
    onRecord: () => void;
    disabled?: boolean;
    transitionMs: number;
}

export const IntroRecordButton: FC<IIntroRecordButtonProps> = ({ onRecord, disabled, transitionMs }) => {
    const classes = useStyles({ transitionMs });
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [animStyle, setAnimStyle] = useState<React.CSSProperties | undefined>();
    const [animStarted, setAnimStarted] = useState(false);

    const handleClick = () => {
        if (disabled) {
            return;
        }

        // Measure the button's current position and place a fixed copy there at full size
        const rect = buttonRef.current?.getBoundingClientRect();
        if (rect) {
            setAnimStyle({
                left: rect.left,
                top: rect.top
            });
        }

        // Sequence:
        // Frame 1: browser paints fixed copy at initial position with transitions
        // Frame 2: consumer callback fires (triggers recording, hides intro, renders app)
        // Frame 3: nav button is in the DOM — measure it and set animation target
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                onRecord();

                requestAnimationFrame(() => {
                    const navButton = document.getElementById('record-btn');
                    const navRect = navButton?.getBoundingClientRect();
                    if (navRect) {
                        setAnimStyle({
                            left: navRect.left + (navRect.width / 2) - 36,
                            top: navRect.top + (navRect.height / 2) - 36,
                        });
                    }
                    setAnimStarted(true);
                });
            });
        });
    };

    return (
        <>
            {/* Placeholder keeps layout stable when button goes fixed */}
            <div className={classes.wrapper}>
                {!animStyle && (
                    <button
                        ref={buttonRef}
                        className={classes.button}
                        onClick={handleClick}
                        data-id="intro-record-button"
                    >
                        <MicrophoneIcon color="light" customSize={28} />
                    </button>
                )}
            </div>
            <div className={classes.label}>
                Tap to start talking
            </div>

            {/* Fixed-position copy animating to nav — portaled to document.body
                so it isn't affected by the parent's fade-out opacity */}
            {animStyle && createPortal(
                <button
                    className={`${classes.button} ${classes.buttonFixed} ${animStarted ? classes.buttonAnimTarget : ''}`}
                    style={animStyle}
                >
                    <MicrophoneIcon color="light" customSize={28} />
                </button>,
                document.body
            )}
        </>
    );
};
